import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { getPinned, togglePin, subscribePinned, setPinned } from "@/lib/pins";
import { getHistory, pushHistory } from "@/lib/compareHistory";
import { X, Flame, Trophy, Star, TrendingUp, History } from "lucide-react";

export default function Compare() {
    const [ids, setIds] = useState(getPinned());
    const [coaches, setCoaches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [history, setHistory] = useState(getHistory());

    useEffect(() => subscribePinned(setIds), []);

    useEffect(() => {
        (async () => {
            setLoading(true);
            const results = await Promise.all(
                ids.map((id) => api.get(`/coaches/${id}`).then((r) => r.data).catch(() => null))
            );
            const live = results.filter(Boolean);
            setCoaches(live);
            setLoading(false);
            // Snapshot into history when the user actually looked at 2+ coachs
            if (live.length >= 2) {
                pushHistory(live.map((c) => c.coach_id));
                setHistory(getHistory());
            }
        })();
    }, [ids]);

    if (ids.length === 0)
        return (
            <div className="mx-auto max-w-[1200px] px-6 py-16">
                <div className="text-center">
                    <h1 className="font-display text-5xl uppercase tracking-tight text-white">
                        Zéro coach épinglé
                    </h1>
                    <p className="mt-4 font-mono text-xs uppercase tracking-widest text-neutral-500">
                        Épingle jusqu&apos;à 3 coachs depuis l&apos;annuaire pour les
                        comparer ici.
                    </p>
                    <Link
                        to="/"
                        className="mt-8 inline-block border border-[#FF2E2E] bg-[#FF2E2E] px-6 py-3 font-display text-lg uppercase tracking-tight text-white hover:bg-[#D61F1F]"
                    >
                        Parcourir l&apos;annuaire
                    </Link>
                </div>
                <HistoryBlock history={history} onRestore={(hid) => setPinned(hid.slice(0, 3))} />
            </div>
        );

    const rows = [
        {
            key: "merit_score",
            label: "Score de mérite",
            icon: Flame,
            fmt: (v) => Math.round(v || 0),
            best: "max",
        },
        {
            key: "goals_achieved",
            label: "Objectifs atteints",
            icon: Trophy,
            fmt: (v) => v,
            best: "max",
        },
        {
            key: "verified_rating",
            label: "Avis vérifiés",
            icon: Star,
            fmt: (v, c) => (v ? `${v.toFixed(1)}/5 (${c?.verified_rating_count})` : "—"),
            best: "max",
        },
        {
            key: "retention_rate",
            label: "Taux de rétention",
            icon: TrendingUp,
            fmt: (v) => `${Math.round((v || 0) * 100)}%`,
            best: "max",
        },
        { key: "hourly_rate", label: "Tarif / h", fmt: (v) => `€${v}`, best: "min" },
        { key: "city", label: "Ville", fmt: (v) => v, best: null },
    ];

    const winnerIndex = (key, best) => {
        if (!best) return -1;
        const vals = coaches.map((c) => c[key] ?? 0);
        const target = best === "max" ? Math.max(...vals) : Math.min(...vals);
        return vals.indexOf(target);
    };

    return (
        <div className="mx-auto max-w-[1400px] px-6 py-10">
            <div className="mb-8">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                    Côte à côte
                </div>
                <h1 className="font-display text-5xl uppercase leading-none tracking-tight text-white sm:text-6xl">
                    Comparer {coaches.length} coach{coaches.length > 1 ? "s" : ""}
                </h1>
            </div>

            {loading ? (
                <div className="h-96 animate-pulse border border-white/10 bg-[#121212]" />
            ) : (
                <div
                    data-testid="compare-grid"
                    className={`grid gap-0 border border-white/10 ${
                        coaches.length === 2
                            ? "grid-cols-2"
                            : coaches.length === 3
                            ? "grid-cols-3"
                            : "grid-cols-1"
                    }`}
                >
                    {coaches.map((c) => (
                        <div
                            key={c.coach_id}
                            data-testid="compare-column"
                            className="flex flex-col border-r border-white/10 last:border-r-0"
                        >
                            <div className="relative aspect-[4/3] overflow-hidden">
                                <img
                                    src={c.photo_url}
                                    alt={c.name}
                                    className="h-full w-full object-cover"
                                />
                                <button
                                    data-testid={`compare-remove-${c.coach_id}`}
                                    onClick={() => togglePin(c.coach_id)}
                                    className="absolute right-2 top-2 border border-white/20 bg-black/60 p-1.5 text-white backdrop-blur-sm hover:border-[#FF2E2E] hover:text-[#FF2E2E]"
                                    aria-label="Retirer"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                                <span
                                    className={`absolute left-2 top-2 font-mono text-[10px] uppercase tracking-[0.3em] px-2 py-1 ${
                                        c.category === "physical"
                                            ? "bg-[#FF2E2E] text-white"
                                            : "border border-white/40 bg-black/60 text-white"
                                    }`}
                                >
                                    {c.category === "physical" ? "Corps" : "Esprit"}
                                </span>
                            </div>
                            <Link
                                to={`/coach/${c.coach_id}`}
                                className="border-b border-white/10 bg-[#121212] p-4 hover:bg-[#1a1a1a]"
                            >
                                <div className="font-display text-2xl uppercase leading-none tracking-tight text-white">
                                    {c.name}
                                </div>
                                <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                                    {c.city} · {c.age} ans
                                </div>
                            </Link>
                        </div>
                    ))}
                </div>
            )}

            {!loading && (
                <div className="mt-0 border-x border-b border-white/10">
                    {rows.map((row) => {
                        const w = winnerIndex(row.key, row.best);
                        return (
                            <div
                                key={row.key}
                                className={`grid ${
                                    coaches.length === 2
                                        ? "grid-cols-[200px_1fr_1fr]"
                                        : "grid-cols-[200px_1fr_1fr_1fr]"
                                } border-t border-white/10`}
                            >
                                <div className="flex items-center gap-2 bg-[#0f0f0f] px-4 py-4 font-mono text-[11px] uppercase tracking-widest text-neutral-400">
                                    {row.icon && <row.icon className="h-3.5 w-3.5 text-[#FF2E2E]" />}
                                    {row.label}
                                </div>
                                {coaches.map((c, i) => (
                                    <div
                                        key={c.coach_id}
                                        className={`border-l border-white/10 px-4 py-4 font-display text-2xl uppercase tracking-tight ${
                                            i === w
                                                ? "bg-[#FF2E2E]/10 text-[#FF2E2E]"
                                                : "text-white"
                                        }`}
                                    >
                                        {row.fmt(c[row.key], c)}
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="mt-8 border border-dashed border-white/10 p-4 text-center text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                Les valeurs surlignées sont les meilleures de la comparaison —
                exclusivement basées sur le mérite.
            </div>

            <HistoryBlock history={history} onRestore={(hid) => setPinned(hid.slice(0, 3))} />
        </div>
    );
}

function HistoryBlock({ history, onRestore }) {
    if (!history || history.length === 0) return null;
    return (
        <div data-testid="compare-history" className="mt-10">
            <div className="mb-4 flex items-center gap-2">
                <History className="h-4 w-4 text-[#FF2E2E]" />
                <h2 className="font-display text-2xl uppercase tracking-tight text-white">
                    Historique
                </h2>
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                    Les 3 dernières comparaisons
                </span>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                {history.map((h) => (
                    <HistoryItem key={h.id} entry={h} onRestore={onRestore} />
                ))}
            </div>
        </div>
    );
}

function HistoryItem({ entry, onRestore }) {
    const [coaches, setCoaches] = useState([]);
    useEffect(() => {
        (async () => {
            const r = await Promise.all(
                entry.ids.map((id) => api.get(`/coaches/${id}`).then((x) => x.data).catch(() => null))
            );
            setCoaches(r.filter(Boolean));
        })();
    }, [entry.id]);

    return (
        <button
            data-testid={`compare-history-restore`}
            onClick={() => onRestore(entry.ids)}
            className="group flex items-center gap-3 border border-white/10 bg-[#121212] p-3 text-left transition-colors hover:border-[#FF2E2E]"
        >
            <div className="flex -space-x-3">
                {coaches.map((c) => (
                    <img
                        key={c.coach_id}
                        src={c.photo_url}
                        alt={c.name}
                        className="h-10 w-10 border border-black object-cover"
                    />
                ))}
            </div>
            <div className="min-w-0 flex-1">
                <div className="truncate font-display text-sm uppercase tracking-tight text-white">
                    {coaches.map((c) => c.name).join(" vs ")}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                    {new Date(entry.at).toLocaleDateString()} ·{" "}
                    <span className="text-[#FF2E2E]">Restaurer →</span>
                </div>
            </div>
        </button>
    );
}
